import { styled } from "styled-components";
import { useNavigate } from "react-router-dom";
import { useTestimonials } from "../hooks/useTestimonials";
import TestimonialSection from "../components/common/TestimonialSection";
import PartnersContainer from "../components/common/PartnersContainer";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${({ theme }) => theme.colors.white};
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 60px 60px;
  width: 100%;
  min-height: calc(100vh - 72px);
  box-sizing: border-box;

  @media (max-width: 1023px) {
    padding: 30px 40px;
    min-height: calc(100vh - 64px);
  }

  @media (max-width: 767px) {
    padding: 20px;
    min-height: calc(100vh - 56px);
  }
`;

const ContentRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 50px;
  width: 100%;
  max-width: 1608px;

  @media (max-width: 1023px) {
    flex-direction: column;
  }
`;

const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 40px;
  flex: 1;
  min-width: 0;

  @media (max-width: 1023px) {
    gap: 30px;
  }

  @media (max-width: 767px) {
    gap: 20px;
  }
`;

const PageTitle = styled.h1`
  font-family: "Sofia Pro", sans-serif;
  font-weight: 600;
  font-size: 40px;
  line-height: 32px;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
  width: 100%;

  @media (max-width: 767px) {
    font-size: 28px;
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 15px;
  width: 100%;
`;

const SectionTitle = styled.h3`
  font-family: "Sofia Pro", sans-serif;
  font-weight: 400;
  font-size: 30px;
  line-height: 32px;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
  width: 100%;

  @media (max-width: 767px) {
    font-size: 24px;
    line-height: 28px;
  }
`;

const BodyText = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 28px;
  color: ${({ theme }) => theme.colors.blue500};
  width: 100%;

  @media (max-width: 767px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const BulletList = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 28px;
  color: ${({ theme }) => theme.colors.blue500};
  width: 100%;

  @media (max-width: 767px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const BulletSection = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const BulletTitle = styled.div`
  font-weight: 700;
  margin-bottom: 5px;
`;

const BulletDescription = styled.div`
  font-weight: 500;
`;

const CTALink = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-weight: 400;
  font-size: 30px;
  line-height: 32px;
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
  text-decoration: underline;
  width: 100%;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 767px) {
    font-size: 24px;
    line-height: 28px;
  }
`;

const AboutUs = () => {
  const { data: testimonials = [] } = useTestimonials();
  const navigate = useNavigate();

  return (
    <PageContainer>
      <ContentArea>
        <ContentRow>
          <LeftContent>
            <PageTitle>O nás</PageTitle>

            <ContentSection>
              <SectionTitle>Vítej na trefim.to,</SectionTitle>
              <BodyText>
                online aukčním domě specializujícím se na holandské aukce. Jsme
                tým odborníků s vášní pro inovativní a vzrušující aukční zážitky,
                kteří chtějí zpřístupnit vybrané kvalitní dárky za výhodných
                podmínek co nejvíce lidem.
              </BodyText>
            </ContentSection>

            <ContentSection>
              <SectionTitle>Naše mise</SectionTitle>
              <BodyText>
                Naše mise je nabídnout transparentní, efektivní a férovou
                platformu pro všechny, kteří chtějí prodat nebo zakoupit různé
                druhy zboží prostřednictvím holandské aukce. Usilujeme o
                vytvoření přátelského a důvěryhodného prostředí, ve kterém můžeš
                snadno a bezpečně získat exkluzivní věci za výhodných podmínek.
              </BodyText>
            </ContentSection>

            <ContentSection>
              <SectionTitle>Proč holandská aukce?</SectionTitle>
              <BodyText>
                Holandská aukce se odlišuje od tradiční aukce tím, že cena
                začíná na běžné prodejní ceně a postupně klesá, dokud ji některý
                z kupujících nepřijme. Tento jedinečný formát aukce přináší
                rychlost a vzrušení, takže si aukce na trefim.to skutečně
                užiješ.
              </BodyText>
            </ContentSection>

            <ContentSection>
              <SectionTitle>Co nabízíme</SectionTitle>
              <BulletList>
                <BulletSection>
                  <BulletTitle>Širokou škálu zboží</BulletTitle>
                  <BulletDescription>
                    Můžeš se těšit na všechno možné. Elektroniku, kosmetiku,
                    cestování, oblečení a mnoho dalšího.
                  </BulletDescription>
                </BulletSection>

                <BulletSection>
                  <BulletTitle>Transparentnost a férovost</BulletTitle>
                  <BulletDescription>
                    Důkladný schvalovací proces. Striktní dodržování pravidel.
                    Na nás se můžeš spolehnout.
                  </BulletDescription>
                </BulletSection>

                <BulletSection>
                  <BulletTitle>Podporu a zákaznický servis</BulletTitle>
                  <BulletDescription>
                    Máš dotaz? Chceš se poradit? Náš tým zákaznické podpory je
                    tu pro tebe.
                  </BulletDescription>
                </BulletSection>

                <BulletSection>
                  <BulletTitle>Bezpečné a jednoduché platby</BulletTitle>
                  <BulletDescription>
                    Na trefim.to platíš snadno, rychle a hlavně bezpečně. To
                    určitě oceníš.
                  </BulletDescription>
                </BulletSection>
              </BulletList>
            </ContentSection>

            <CTALink onClick={() => navigate("/register")}>
              Zaregistruj se zdarma a začni si užívat všech těchto výhod ještě
              dnes!
            </CTALink>
          </LeftContent>
        </ContentRow>
      </ContentArea>

      <TestimonialSection testimonials={testimonials} />
      <PartnersContainer />
    </PageContainer>
  );
};

export default AboutUs;
